/**
 * Mark done… (v1 completion I §9, the mock-up's state 3): say what you did, see
 * what happens (the dry run's notes, the server's words), then mark it done at
 * the command's own href. A refused note is bound to the note field; a refusal
 * of the whole command (it cleared meanwhile) shows as the banner.
 */
import { useEffect, useState } from "react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { AlertCommand } from "@shared-core/data/needs-attention-types";
import { extractFieldErrors } from "@shared-core/data/validation";
import { refusalWords } from "@shared-core/domain/needs-attention";
import { Button } from "../../primitives/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../primitives/dialog";
import { Label } from "../../primitives/label";
import { Textarea } from "../../primitives/textarea";

export interface MarkAlertDoneDialogProps {
  command: AlertCommand;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** After the commit: the server's summary, to toast, and re-read what is on screen. */
  onDone: (summary: string) => void;
}

export function MarkAlertDoneDialog({
  command,
  open,
  onOpenChange,
  onDone,
}: MarkAlertDoneDialogProps) {
  const adapter = useDataAdapter();
  const [note, setNote] = useState("");
  const [notes, setNotes] = useState<string[]>([]);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setNote("");
    setNoteError(null);
    setBanner(null);
    adapter
      .dryRunAlertCommand(command, {})
      .then((preview) => {
        if (!cancelled)
          setNotes(
            preview.display.flatMap((block) =>
              block.kind === "note" ? block.lines : [],
            ),
          );
      })
      .catch((err: unknown) => {
        if (!cancelled)
          setBanner(refusalWords(err) ?? "Mark done is not available now.");
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, command, open]);

  const submit = async () => {
    setIsSaving(true);
    setNoteError(null);
    setBanner(null);
    try {
      const result = await adapter.runAlertCommand(
        command,
        note.trim() ? { note: note.trim() } : {},
      );
      onOpenChange(false);
      onDone(result.summary);
    } catch (err) {
      const fields = extractFieldErrors(err);
      const onNote = fields.find((f) => f.field === "note");
      if (onNote) {
        setNoteError(onNote.message);
        setBanner("1 field needs a fix: see below.");
      } else {
        setBanner(refusalWords(err) ?? "It was not marked done. Try again.");
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Mark done</DialogTitle>
          <DialogDescription>
            Say what you did, if it helps the next person.
          </DialogDescription>
        </DialogHeader>
        {banner && (
          <p
            role="alert"
            className="rounded-md border border-destructive px-3 py-2 text-sm text-destructive"
          >
            {banner}
          </p>
        )}
        <div className="space-y-1">
          <Label htmlFor="mark-done-note">
            Note{" "}
            <span className="font-normal text-muted-foreground">
              (optional)
            </span>
          </Label>
          <Textarea
            id="mark-done-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-invalid={noteError ? true : undefined}
            aria-describedby={noteError ? "mark-done-note-error" : undefined}
          />
          {noteError && (
            <p id="mark-done-note-error" className="text-sm text-red-600">
              {noteError}
            </p>
          )}
        </div>
        {notes.length > 0 && (
          <div className="rounded-md border border-dashed border-border bg-muted/40 px-3 py-2 text-sm">
            <p className="font-medium">What happens</p>
            {notes.map((line) => (
              <p key={line}>{line}</p>
            ))}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={isSaving}>
            Mark done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
